import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Search, Trophy, Target, Shield, Users, ArrowRight, TrendingUp, 
  MapPin, Star, Flame, Award, Zap, ChevronRight, BarChart3, Newspaper, Calendar,
  Radio, ExternalLink, Clock, Bell
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/hooks/useSEO";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TeamLogo } from "@/lib/teamUtils";
import { AdBanner } from "@/components/AdBanner";
import LiveMatchTicker from "@/components/LiveMatchTicker";
import MyTeamsSection from "@/components/MyTeamsSection";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState("2026");
  const [stats, setStats] = useState(null);

  useSEO({
    title: `Fotbollsliv.com — Sveriges Fotbollsranking ${selectedSeason} | Super-maratontabell`,
    description: `Sveriges mest kompletta fotbollsranking ${selectedSeason}. Se alla ${stats?.total_teams || '1000+'} lag från Allsvenskan till Division 5 rankade efter viktade poäng. Sök, jämför och följ ditt lag.`,
  });
  const [topTeams, setTopTeams] = useState([]);
  const [teamOfWeek, setTeamOfWeek] = useState(null);
  const [weekData, setWeekData] = useState(null);
  const [highlights, setHighlights] = useState([]);
  const [latestPosts, setLatestPosts] = useState([]);
  const [latestNews, setLatestNews] = useState([]);
  const [hotTeams, setHotTeams] = useState([]);
  const [coldTeams, setColdTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Use allSettled so one failing endpoint doesn't break all stats
        const [statsRes, topRes, weekRes, blogRes, newsRes, hotRes] = await Promise.allSettled([
          axios.get(`${API}/stats/overview?season=${selectedSeason}`),
          axios.get(`${API}/top-teams?limit=10&season=${selectedSeason}`),
          axios.get(`${API}/team-of-the-week?season=${selectedSeason}`),
          axios.get(`${API}/blog/latest?limit=3`),
          axios.get(`${API}/news?limit=5`),
          axios.get(`${API}/hot-teams?limit=10&season=${selectedSeason}`),
        ]);
        if (statsRes.status === "fulfilled") setStats(statsRes.value.data);
        if (topRes.status === "fulfilled") setTopTeams(topRes.value.data.teams);
        if (weekRes.status === "fulfilled") {
          setTeamOfWeek(weekRes.value.data.team_of_week);
          setWeekData(weekRes.value.data);
          setHighlights(weekRes.value.data.highlights || []);
        }
        if (blogRes.status === "fulfilled") setLatestPosts(blogRes.value.data.posts || []);
        if (newsRes.status === "fulfilled") setLatestNews(newsRes.value.data.items || []);
        if (hotRes.status === "fulfilled") {
          setHotTeams(hotRes.value.data.hot || []);
          setColdTeams(hotRes.value.data.cold || []);
        }
      } catch (e) {
        console.error("Failed to fetch data:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedSeason]);

  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/teams/search/${encodeURIComponent(searchQuery)}`);
        setSearchResults(res.data.results);
        setShowResults(true);
      } catch (e) {
        console.error(e);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/tabell?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const getDivisionColor = (div) => {
    const colors = {
      "Allsvenskan": "bg-[#FECC00] text-[#0F172A]",
      "Superettan": "bg-slate-700 text-white",
      "Division 1": "bg-blue-600 text-white",
      "Division 2": "bg-green-600 text-white",
      "Division 3": "bg-purple-600 text-white",
      "Division 4": "bg-orange-500 text-white",
      "Division 5": "bg-teal-600 text-white",
    };
    return colors[div] || "bg-slate-500 text-white";
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return "rank-gold font-bold";
    if (rank === 2) return "rank-silver font-bold";
    if (rank === 3) return "rank-bronze font-bold";
    return "text-slate-500";
  };

  const getHighlightIcon = (type) => {
    const icons = {
      "best_goal_diff": <Zap className="w-4 h-4" />,
      "most_goals": <Flame className="w-4 h-4" />,
      "best_defense": <Shield className="w-4 h-4" />,
      "highest_win_rate": <Award className="w-4 h-4" />,
    };
    return icons[type] || <Star className="w-4 h-4" />;
  };

  return (
    <div data-testid="home-page" className="bg-gradient-to-b from-slate-50 to-white">
      {/* Live Match Ticker - Topmost position */}
      <LiveMatchTicker />

      {/* Personalized: My Teams (only for logged-in users with followed teams) */}
      <MyTeamsSection />

      {/* Header Ad Banner */}
      <AdBanner position="header" className="mb-0" />
      
      {/* Hero Section - Enhanced with gradient overlay */}
      <section
        className="relative overflow-hidden min-h-[280px] md:min-h-[420px]"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1522778119026-d647f0565c6d?q=80&w=2940&auto=format&fit=crop')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Gradient overlay with green tint for football feel */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A]/95 via-[#0F172A]/90 to-emerald-900/80" />
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#FECC00]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-24">
          <div className="max-w-2xl">
            {/* Season Selector Badge */}
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-3 py-1.5">
                <span className="w-2 h-2 bg-[#FECC00] rounded-full animate-pulse" />
                <span className="text-xs font-medium text-white/90">Säsong</span>
              </div>
              <Select value={selectedSeason} onValueChange={setSelectedSeason}>
                <SelectTrigger className="w-28 h-8 bg-white/10 backdrop-blur-sm border-white/20 text-white text-sm font-semibold rounded-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <h1
              className="text-3xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-tight"
              style={{ fontFamily: 'Chivo, sans-serif' }}
              data-testid="hero-title"
            >
              Hela Sveriges
              <span className="text-[#FECC00] block">fotbollsranking</span>
            </h1>
            <p className="mt-3 md:mt-4 text-sm md:text-lg text-slate-300 max-w-lg leading-relaxed">
              Super-maratontabell för svensk herrfotboll. Hitta din klubb bland{" "}
              <span className="text-white font-semibold">{stats?.total_teams || "1000+"} lag</span>
              {" "}- från Allsvenskan till Division 5.
            </p>

            {/* Search bar - Enhanced */}
            <form
              onSubmit={handleSearchSubmit}
              className="mt-5 md:mt-8 relative max-w-md"
              data-testid="hero-search-form"
            >
              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-[#FECC00] to-emerald-500 rounded-xl blur opacity-30 group-hover:opacity-50 transition duration-300" />
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Sök efter din klubb..."
                    className="pl-12 pr-4 h-12 bg-white border-0 text-[#0F172A] placeholder:text-slate-400 rounded-xl text-sm shadow-lg"
                    data-testid="hero-search-input"
                    onBlur={() => setTimeout(() => setShowResults(false), 200)}
                    onFocus={() => searchResults.length > 0 && setShowResults(true)}
                  />
                </div>
              </div>

              {showResults && searchResults.length > 0 && (
                <div
                  className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden"
                  data-testid="search-dropdown"
                >
                  {searchResults.map((team) => (
                    <Link
                      key={team.team_id}
                      to={`/lag/${team.team_id}`}
                      className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0"
                      data-testid={`search-result-${team.team_id}`}
                      onClick={() => {
                        if (!isAuthenticated) {
                          setTimeout(() => {
                            toast(
                              `Vill du följa ${team.team_name} och få uppdateringar?`,
                              {
                                description: "Skapa ett gratis konto och få notiser vid rankingförändringar.",
                                action: { label: "Skapa konto", onClick: () => navigate('/registrera') },
                                duration: 6000,
                              }
                            );
                          }, 1200);
                        }
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <TeamLogo teamName={team.team_name} division={team.division} size="sm" logoUrl={team.logo_url} />
                        <div>
                          <span className="text-sm font-semibold text-[#0F172A]">{team.team_name}</span>
                          <span className="text-xs text-slate-400 ml-2 flex items-center gap-1 inline-flex">
                            <MapPin className="w-3 h-3" /> {team.county}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getDivisionColor(team.division)}>
                          {team.division}
                        </Badge>
                        <span className="text-xs font-mono text-slate-400">#{team.rank}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </form>

            {/* Value exchange CTA for non-authenticated users */}
            {!isAuthenticated && (
              <div className="mt-4 md:mt-5 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-3 md:px-4 py-2.5 md:py-3 max-w-md" data-testid="hero-signup-cta">
                <div className="flex items-center gap-2 text-xs md:text-sm text-slate-300">
                  <Star className="w-4 h-4 text-[#FECC00] shrink-0" />
                  <span>Spara favoritlag och få notiser</span>
                </div>
                <Button
                  size="sm"
                  className="shrink-0 bg-[#FECC00] hover:bg-yellow-400 text-[#0F172A] font-semibold text-xs h-8 px-3 rounded-lg"
                  onClick={() => navigate('/registrera')}
                  data-testid="hero-signup-btn"
                >
                  Skapa gratis konto
                </Button>
              </div>
            )}

            <div className="mt-5 flex items-center gap-2 md:gap-3">
              <Button
                asChild
                className="bg-[#FECC00] text-[#0F172A] hover:bg-[#e5b800] font-bold rounded-lg px-4 md:px-6 h-10 md:h-11 text-xs md:text-sm shadow-lg shadow-[#FECC00]/20"
                data-testid="hero-cta-button"
              >
                <Link to={`/tabell?season=${selectedSeason}`}>
                  Se hela tabellen <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-lg px-4 md:px-5 h-10 md:h-11 text-xs md:text-sm backdrop-blur-sm"
              >
                <Link to="/om">
                  Så fungerar det
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats - Enhanced with better cards */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 -mt-8 relative z-10 min-h-[90px] md:min-h-[130px]" data-testid="quick-stats">
        <div className="grid grid-cols-3 gap-2 md:gap-4">
          <Card className="bg-white border-0 shadow-xl shadow-slate-200/50 hover:shadow-2xl transition-shadow duration-300">
            <CardContent className="p-3 md:p-6">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-2 md:gap-4">
                <div className="p-2 md:p-3 bg-gradient-to-br from-[#FECC00] to-amber-500 rounded-lg md:rounded-xl shadow-lg shadow-amber-200">
                  <Trophy className="w-4 h-4 md:w-6 md:h-6 text-white" />
                </div>
                <div className="text-center md:text-left">
                  <p className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wider font-semibold">Topplacerad</p>
                  {stats?.top_team ? (
                    <>
                      <p className="text-sm md:text-xl font-black text-[#0F172A] mt-0.5 md:mt-1 leading-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        {stats.top_team.team_name}
                      </p>
                      <p className="text-xs md:text-sm text-slate-500 mt-0.5 md:mt-1 font-mono">
                        {stats.top_team.weighted_points} <span className="text-[10px] md:text-xs">VP</span>
                      </p>
                    </>
                  ) : (
                    <div className="h-6 w-20 md:w-32 skeleton-shimmer rounded mt-1" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-xl shadow-slate-200/50 hover:shadow-2xl transition-shadow duration-300">
            <CardContent className="p-3 md:p-6">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-2 md:gap-4">
                <div className="p-2 md:p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg md:rounded-xl shadow-lg shadow-emerald-200">
                  <Target className="w-4 h-4 md:w-6 md:h-6 text-white" />
                </div>
                <div className="text-center md:text-left">
                  <p className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wider font-semibold">Flest mål</p>
                  {stats?.most_goals ? (
                    <>
                      <p className="text-sm md:text-xl font-black text-[#0F172A] mt-0.5 md:mt-1 leading-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        {stats.most_goals.team_name}
                      </p>
                      <p className="text-xs md:text-sm text-slate-500 mt-0.5 md:mt-1 font-mono">
                        {stats.most_goals.goals_for} <span className="text-[10px] md:text-xs">mål</span>
                      </p>
                    </>
                  ) : (
                    <div className="h-6 w-20 md:w-32 skeleton-shimmer rounded mt-1" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-xl shadow-slate-200/50 hover:shadow-2xl transition-shadow duration-300">
            <CardContent className="p-3 md:p-6">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-2 md:gap-4">
                <div className="p-2 md:p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg md:rounded-xl shadow-lg shadow-blue-200">
                  <Shield className="w-4 h-4 md:w-6 md:h-6 text-white" />
                </div>
                <div className="text-center md:text-left">
                  <p className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wider font-semibold">Bäst försvar</p>
                  {stats?.best_defense ? (
                    <>
                      <p className="text-sm md:text-xl font-black text-[#0F172A] mt-0.5 md:mt-1 leading-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        {stats.best_defense.team_name}
                      </p>
                      <p className="text-xs md:text-sm text-slate-500 mt-0.5 md:mt-1 font-mono">
                        {stats.best_defense.goals_against} <span className="text-[10px] md:text-xs">insläppta</span>
                      </p>
                    </>
                  ) : (
                    <div className="h-6 w-20 md:w-32 skeleton-shimmer rounded mt-1" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Team of the Week Section */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 mt-8 md:mt-12 min-h-[280px] md:min-h-[340px]" data-testid="team-of-week-section">
        {teamOfWeek ? (
          <div className="bg-gradient-to-br from-[#0F172A] to-slate-800 rounded-2xl overflow-hidden shadow-2xl">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Left side - Team of Week */}
              <div className="p-5 md:p-10 relative">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#FECC00]/10 rounded-full blur-3xl" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-4">
                    <Star className="w-5 h-5 text-[#FECC00]" />
                    <span className="text-[#FECC00] text-sm font-bold uppercase tracking-wider">Veckans lag (Vecka {weekData?.week_number || ''})</span>
                  </div>
                  
                  <Link to={`/lag/${teamOfWeek.team_id}`} className="group">
                    <div className="flex items-center gap-4 mb-4">
                      <TeamLogo teamName={teamOfWeek.team_name} division={teamOfWeek.division} size="xl" logoUrl={teamOfWeek.logo_url} />
                      <div>
                        <h3 className="text-2xl md:text-3xl font-black text-white group-hover:text-[#FECC00] transition-colors" style={{ fontFamily: 'Chivo, sans-serif' }}>
                          {teamOfWeek.team_name}
                        </h3>
                        <Badge className={`${getDivisionColor(teamOfWeek.division)} mt-1`}>
                          {teamOfWeek.division}
                        </Badge>
                      </div>
                    </div>
                  </Link>
                  
                  <p className="text-sm text-slate-400 mb-4">
                    Slumpmässigt utvalt bland {weekData?.total_teams_in_pool || 0} lag
                  </p>
                  
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    <div className="text-center p-3 bg-white/5 rounded-lg">
                      <p className="text-2xl font-black text-[#FECC00]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        {teamOfWeek.weighted_points || 0}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Viktade poäng</p>
                    </div>
                    <div className="text-center p-3 bg-white/5 rounded-lg">
                      <p className="text-2xl font-black text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        {teamOfWeek.wins || 0}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Vinster</p>
                    </div>
                    <div className="text-center p-3 bg-white/5 rounded-lg">
                      <p className="text-2xl font-black text-emerald-400" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        {teamOfWeek.goal_difference >= 0 ? '+' : ''}{teamOfWeek.goal_difference || 0}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Målskillnad</p>
                    </div>
                  </div>
                  
                  <Link 
                    to={`/lag/${teamOfWeek.team_id}`}
                    className="inline-flex items-center gap-2 text-sm text-[#FECC00] hover:text-white mt-6 font-semibold transition-colors"
                  >
                    Se fullständig statistik <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
              
              {/* Right side - Highlights */}
              <div className="bg-slate-900/50 p-5 md:p-10">
                <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">
                  Veckans höjdpunkter
                </h4>
                <div className="space-y-3">
                  {highlights.map((h, i) => (
                    <Link 
                      key={i} 
                      to={`/lag/${h.team_id}`}
                      className="flex items-center gap-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors group"
                    >
                      <div className="p-2 bg-[#FECC00]/20 rounded-lg text-[#FECC00]">
                        {getHighlightIcon(h.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-400">{h.label}</p>
                        <p className="text-white font-semibold truncate group-hover:text-[#FECC00] transition-colors">
                          {h.team}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-[#FECC00]">{h.value}</p>
                        <p className="text-xs text-slate-500">{h.division}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-slate-200 rounded-2xl animate-pulse h-[280px] md:h-[340px]" />
        )}
      </section>

      {/* Latest News Section */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 mt-8 md:mt-12 min-h-[200px] md:min-h-[240px]" data-testid="news-section">
        {latestNews.length > 0 ? (
          <>
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-red-500 rounded-lg">
                <Radio className="w-4 h-4 md:w-5 md:h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-black text-[#0F172A]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Senaste nyheterna
                </h2>
                <p className="text-slate-500 text-sm">Live från svenska sportmedier</p>
              </div>
            </div>
            <Button asChild variant="outline" className="hidden sm:flex">
              <Link to="/nyheter">
                Alla nyheter <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {latestNews.slice(0, 6).map((item) => (
              <a
                key={item.id}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group"
                data-testid={`home-news-${item.id}`}
              >
                <Card className="overflow-hidden h-full hover:shadow-lg transition-all duration-300 border-0 shadow-md hover:-translate-y-0.5">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`text-xs ${
                        item.source_id === 'fotbolldirekt' ? 'bg-green-600' :
                        item.source_id === 'bollsvenskan' ? 'bg-blue-600' :
                        'bg-red-600'
                      } text-white`}>
                        {item.source}
                      </Badge>
                      {item.published && (
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {(() => {
                            const date = new Date(item.published);
                            const now = new Date();
                            const diffMins = Math.floor((now - date) / 60000);
                            const diffHours = Math.floor((now - date) / 3600000);
                            if (diffMins < 60) return `${diffMins} min`;
                            if (diffHours < 24) return `${diffHours} tim`;
                            return date.toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
                          })()}
                        </span>
                      )}
                    </div>
                    <h3 className="font-bold text-[#0F172A] group-hover:text-[#FECC00] transition-colors line-clamp-2 text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {item.title}
                    </h3>
                    <div className="flex items-center text-xs text-slate-500 mt-2 group-hover:text-[#FECC00] transition-colors">
                      Läs mer <ExternalLink className="w-3 h-3 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
          
          <div className="mt-4 text-center sm:hidden">
            <Button asChild variant="outline">
              <Link to="/nyheter">
                Alla nyheter <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
          </>
        ) : loading ? (
          <div className="space-y-3">
            <div className="h-6 w-48 bg-slate-200 rounded animate-pulse" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3].map(i => <div key={i} className="h-28 bg-slate-200 rounded-xl animate-pulse" />)}
            </div>
          </div>
        ) : null}
      </section>

      {/* Overview Numbers */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 mt-8 md:mt-12 min-h-[100px] md:min-h-[120px]">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-4 md:p-8">
          <div className="grid grid-cols-2 md:flex md:items-center md:justify-center gap-4 md:gap-16">
            <div className="text-center">
              <p className="text-2xl md:text-5xl font-black text-[#0F172A]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {stats?.total_teams || "-"}
              </p>
              <p className="text-xs md:text-sm text-slate-500 mt-1 font-medium">Lag totalt</p>
            </div>
            <div className="w-px h-12 bg-slate-200 hidden md:block" />
            <div className="text-center">
              <p className="text-2xl md:text-5xl font-black text-[#0F172A]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {Object.keys(stats?.divisions_count || {}).length || "-"}
              </p>
              <p className="text-xs md:text-sm text-slate-500 mt-1 font-medium">Divisioner</p>
            </div>
            <div className="w-px h-12 bg-slate-200 hidden md:block" />
            <div className="text-center">
              <p className="text-2xl md:text-5xl font-black text-emerald-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {stats?.total_goals?.toLocaleString() || "-"}
              </p>
              <p className="text-xs md:text-sm text-slate-500 mt-1 font-medium">Mål gjorda</p>
            </div>
            <div className="w-px h-12 bg-slate-200 hidden md:block" />
            <div className="text-center">
              <p className="text-2xl md:text-5xl font-black text-blue-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {stats?.total_matches?.toLocaleString() || "-"}
              </p>
              <p className="text-xs md:text-sm text-slate-500 mt-1 font-medium">Matcher</p>
            </div>
          </div>
        </div>
      </section>

      {/* Table Row Ad */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 mt-8">
        <AdBanner position="table-row" />
      </div>

      {/* Top 10 Preview - Enhanced */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 mt-8 md:mt-12 mb-8 md:mb-16" data-testid="top-10-section">
        <Card className="bg-white border-0 shadow-xl overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-[#0F172A] to-slate-800 text-white p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-[#FECC00] rounded-lg">
                  <Trophy className="w-4 h-4 md:w-5 md:h-5 text-[#0F172A]" />
                </div>
                <div>
                  <CardTitle className="text-base md:text-xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Topp 10 i Sverige
                  </CardTitle>
                  <p className="text-slate-400 text-sm mt-0.5">Baserat på viktade poäng</p>
                </div>
              </div>
              <Button
                asChild
                variant="secondary"
                className="bg-[#FECC00] text-[#0F172A] hover:bg-[#e5b800] font-semibold"
              >
                <Link to={`/tabell?season=${selectedSeason}`}>
                  Visa alla <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-0">
                <div className="bg-slate-50 border-b px-4 py-3 flex gap-4">
                  <div className="h-4 w-8 bg-slate-200 rounded animate-pulse" />
                  <div className="h-4 w-40 bg-slate-200 rounded animate-pulse" />
                  <div className="flex-1" />
                  <div className="h-4 w-12 bg-slate-200 rounded animate-pulse" />
                </div>
                {[...Array(10)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4 px-4 py-4 border-b border-slate-100">
                    <div className="h-5 w-6 bg-slate-200 rounded animate-pulse" />
                    <div className="h-8 w-8 bg-slate-200 rounded-full animate-pulse" />
                    <div className="h-4 w-32 bg-slate-200 rounded animate-pulse" />
                    <div className="flex-1" />
                    <div className="h-5 w-10 bg-slate-200 rounded animate-pulse" />
                  </div>
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 border-b border-slate-200">
                    <TableHead className="w-16 text-center font-bold text-slate-600">#</TableHead>
                    <TableHead className="font-bold text-slate-600">Lag</TableHead>
                    <TableHead className="text-center font-bold text-slate-600 hidden sm:table-cell">S</TableHead>
                    <TableHead className="text-center font-bold text-slate-600 hidden sm:table-cell">V</TableHead>
                    <TableHead className="text-center font-bold text-slate-600 hidden sm:table-cell">O</TableHead>
                    <TableHead className="text-center font-bold text-slate-600 hidden sm:table-cell">F</TableHead>
                    <TableHead className="text-center font-bold text-slate-600 hidden md:table-cell">+/-</TableHead>
                    <TableHead className="text-right font-bold text-slate-600 pr-6">VP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topTeams.map((team, i) => (
                    <TableRow
                      key={team.team_id}
                      className="hover:bg-slate-50/80 transition-colors cursor-pointer border-b border-slate-100"
                      onClick={() => navigate(`/lag/${team.team_id}`)}
                      data-testid={`top-team-${team.team_id}`}
                    >
                      <TableCell className="text-center py-4">
                        <span className={`text-lg font-bold ${getRankStyle(i + 1)}`}>
                          {i + 1}
                        </span>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex items-center gap-3">
                          <TeamLogo teamName={team.team_name} division={team.division} size="md" logoUrl={team.logo_url} />
                          <div>
                            <p className="font-bold text-[#0F172A]">{team.team_name}</p>
                            <Badge className={`${getDivisionColor(team.division)} text-xs mt-0.5`}>
                              {team.division}
                            </Badge>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-sm text-slate-600 hidden sm:table-cell">
                        {team.matches_played}
                      </TableCell>
                      <TableCell className="text-center text-sm font-semibold text-emerald-600 hidden sm:table-cell">
                        {team.wins}
                      </TableCell>
                      <TableCell className="text-center text-sm text-slate-500 hidden sm:table-cell">
                        {team.draws}
                      </TableCell>
                      <TableCell className="text-center text-sm text-red-500 hidden sm:table-cell">
                        {team.losses}
                      </TableCell>
                      <TableCell className="text-center text-sm hidden md:table-cell">
                        <span className={team.goal_difference > 0 ? "text-emerald-600 font-semibold" : team.goal_difference < 0 ? "text-red-500" : "text-slate-400"}>
                          {team.goal_difference > 0 ? `+${team.goal_difference}` : team.goal_difference}
                        </span>
                      </TableCell>
                      <TableCell className="text-right pr-6">
                        <span className="text-lg font-black text-[#0F172A]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                          {team.weighted_points}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </section>


      {/* Hot Teams Section */}
      {hotTeams.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 mt-8 md:mt-12" data-testid="hot-teams-section">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Hottest */}
            <Card className="border border-slate-200 shadow-sm overflow-hidden">
              <CardContent className="p-0">
                <div className="flex items-center gap-2 px-4 pt-4 pb-3">
                  <div className="p-1.5 bg-orange-100 rounded-lg">
                    <Flame className="w-4 h-4 text-orange-600" />
                  </div>
                  <h2 className="text-base md:text-lg font-black text-[#0F172A]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Hetaste lagen
                  </h2>
                </div>
                <div className="divide-y divide-slate-100">
                  {hotTeams.slice(0, 5).map((team, i) => (
                    <Link key={team.team_id} to={`/lag/${team.team_id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors" data-testid={`hot-team-${i}`}>
                      <span className="text-sm font-bold text-orange-500 w-5">{i + 1}</span>
                      <TeamLogo teamName={team.team_name} division={team.division} size="sm" logoUrl={team.logo_url} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#0F172A] truncate">{team.team_name}</p>
                        <p className="text-[10px] text-slate-400">{team.division}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {team.form?.split(",").map((r, j) => (
                          <span key={j} className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${
                            r.trim() === "W" ? "bg-green-100 text-green-700" :
                            r.trim() === "D" ? "bg-slate-100 text-slate-500" :
                            "bg-red-100 text-red-600"
                          }`}>{r.trim()}</span>
                        ))}
                      </div>
                      {team.streak?.count >= 3 && team.streak?.type === "W" && (
                        <Badge variant="outline" className="text-[10px] border-green-200 text-green-700 shrink-0">
                          {team.streak.count}V i rad
                        </Badge>
                      )}
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Coldest */}
            <Card className="border border-slate-200 shadow-sm overflow-hidden">
              <CardContent className="p-0">
                <div className="flex items-center gap-2 px-4 pt-4 pb-3">
                  <div className="p-1.5 bg-blue-100 rounded-lg">
                    <TrendingUp className="w-4 h-4 text-blue-600 rotate-180" />
                  </div>
                  <h2 className="text-base md:text-lg font-black text-[#0F172A]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Tyngst just nu
                  </h2>
                </div>
                <div className="divide-y divide-slate-100">
                  {coldTeams.slice(0, 5).map((team, i) => (
                    <Link key={team.team_id} to={`/lag/${team.team_id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors" data-testid={`cold-team-${i}`}>
                      <span className="text-sm font-bold text-blue-400 w-5">{i + 1}</span>
                      <TeamLogo teamName={team.team_name} division={team.division} size="sm" logoUrl={team.logo_url} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#0F172A] truncate">{team.team_name}</p>
                        <p className="text-[10px] text-slate-400">{team.division}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {team.form?.split(",").map((r, j) => (
                          <span key={j} className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${
                            r.trim() === "W" ? "bg-green-100 text-green-700" :
                            r.trim() === "D" ? "bg-slate-100 text-slate-500" :
                            "bg-red-100 text-red-600"
                          }`}>{r.trim()}</span>
                        ))}
                      </div>
                      {team.streak?.count >= 3 && team.streak?.type === "L" && (
                        <Badge variant="outline" className="text-[10px] border-red-200 text-red-600 shrink-0">
                          {team.streak.count}F i rad
                        </Badge>
                      )}
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}


      {/* Blog Section */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 mt-8 md:mt-12 min-h-[280px] md:min-h-[360px]" data-testid="blog-section">
        {latestPosts.length > 0 ? (
          <>
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-[#FECC00] rounded-lg">
                <Newspaper className="w-4 h-4 md:w-5 md:h-5 text-[#0F172A]" />
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-black text-[#0F172A]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Senaste från bloggen
                </h2>
                <p className="text-slate-500 text-sm">Nyheter och analyser</p>
              </div>
            </div>
            <Button asChild variant="outline" className="hidden sm:flex">
              <Link to="/blogg">
                Alla inlägg <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {latestPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blogg/${post.id}`}
                className="group"
                data-testid={`home-blog-post-${post.id}`}
              >
                <Card className="overflow-hidden h-full hover:shadow-xl transition-all duration-300 border-0 shadow-md hover:-translate-y-1">
                  {post.image_url ? (
                    <div className="h-40 overflow-hidden">
                      <img
                        src={post.image_url}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="h-40 bg-gradient-to-br from-[#0F172A] to-slate-700 flex items-center justify-center">
                      <Newspaper className="w-12 h-12 text-slate-500" />
                    </div>
                  )}
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      {post.category && (
                        <Badge className="bg-[#FECC00] text-[#0F172A] text-xs">
                          {post.category}
                        </Badge>
                      )}
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(post.created_at).toLocaleDateString("sv-SE")}
                      </span>
                    </div>
                    <h3 
                      className="font-bold text-[#0F172A] group-hover:text-[#FECC00] transition-colors line-clamp-2"
                      style={{ fontFamily: 'Chivo, sans-serif' }}
                    >
                      {post.title}
                    </h3>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          
          <div className="mt-6 text-center sm:hidden">
            <Button asChild variant="outline">
              <Link to="/blogg">
                Alla inlägg <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
          </>
        ) : loading ? (
          <div className="space-y-3">
            <div className="h-6 w-36 bg-slate-200 rounded animate-pulse" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1,2,3,4].map(i => <div key={i} className="h-[200px] bg-slate-200 rounded-xl animate-pulse" />)}
            </div>
          </div>
        ) : null}
      </section>

      {/* Data Visualization Section */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 mt-8 md:mt-12" data-testid="data-visualization-section">
        <Card className="bg-white border-0 shadow-xl overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#FECC00] rounded-lg">
                <BarChart3 className="w-5 h-5 text-[#0F172A]" />
              </div>
              <div>
                <CardTitle className="text-xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Statistiköversikt
                </CardTitle>
                <p className="text-slate-400 text-sm mt-0.5">Fördelning av lag per division</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 md:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              {/* Pie Chart */}
              <div className="h-56 md:h-80">
                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-4">Lagfördelning</h3>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: "Allsvenskan", value: stats?.divisions_count?.["Allsvenskan"] || 0, fill: "#FECC00" },
                        { name: "Superettan", value: stats?.divisions_count?.["Superettan"] || 0, fill: "#475569" },
                        { name: "Division 1", value: stats?.divisions_count?.["Division 1"] || 0, fill: "#3B82F6" },
                        { name: "Division 2", value: stats?.divisions_count?.["Division 2"] || 0, fill: "#10B981" },
                        { name: "Division 3", value: stats?.divisions_count?.["Division 3"] || 0, fill: "#8B5CF6" },
                        { name: "Division 4", value: stats?.divisions_count?.["Division 4"] || 0, fill: "#F97316" },
                        { name: "Division 5", value: stats?.divisions_count?.["Division 5"] || 0, fill: "#0D9488" },
                      ].filter(d => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={3}
                      dataKey="value"
                      label={false}
                    >
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1E293B', 
                        border: 'none',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value, name) => [`${value} lag`, name]}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Bar Chart */}
              <div className="h-56 md:h-80">
                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-4">Antal lag per division</h3>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      { division: "Allsvenskan", lag: stats?.divisions_count?.["Allsvenskan"] || 0 },
                      { division: "Superettan", lag: stats?.divisions_count?.["Superettan"] || 0 },
                      { division: "Div 1", lag: stats?.divisions_count?.["Division 1"] || 0 },
                      { division: "Div 2", lag: stats?.divisions_count?.["Division 2"] || 0 },
                      { division: "Div 3", lag: stats?.divisions_count?.["Division 3"] || 0 },
                      { division: "Div 4", lag: stats?.divisions_count?.["Division 4"] || 0 },
                      { division: "Div 5", lag: stats?.divisions_count?.["Division 5"] || 0 },
                    ]}
                    margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="division" 
                      stroke="#64748b" 
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis 
                      stroke="#64748b" 
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1E293B', 
                        border: 'none',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value) => [`${value} lag`, "Antal"]}
                    />
                    <Bar 
                      dataKey="lag" 
                      fill="#FECC00" 
                      radius={[4, 4, 0, 0]}
                      name="Lag"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Summary stats */}
            <div className="mt-6 pt-6 border-t border-slate-200 grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-black text-[#0F172A]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {stats?.total_teams || "-"}
                </p>
                <p className="text-xs text-slate-500 mt-1">Totalt antal lag</p>
              </div>
              <div>
                <p className="text-2xl font-black text-emerald-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {stats?.total_goals?.toLocaleString() || "-"}
                </p>
                <p className="text-xs text-slate-500 mt-1">Gjorda mål</p>
              </div>
              <div>
                <p className="text-2xl font-black text-blue-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {stats?.total_matches?.toLocaleString() || "-"}
                </p>
                <p className="text-xs text-slate-500 mt-1">Spelade matcher</p>
              </div>
              <div>
                <p className="text-2xl font-black text-purple-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {Object.keys(stats?.divisions_count || {}).length || "-"}
                </p>
                <p className="text-xs text-slate-500 mt-1">Aktiva divisioner</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Division Overview - Enhanced */}
      <section className="bg-gradient-to-b from-slate-100 to-slate-50 py-10 md:py-16 mt-8 md:mt-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-6 md:mb-10">
            <h2 className="text-xl md:text-3xl font-black text-[#0F172A]" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Lag per division
            </h2>
            <p className="text-slate-500 mt-2">Alla divisioner i svensk herrfotboll</p>
          </div>
          
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2 md:gap-4">
            {[
              { name: "Allsvenskan", color: "from-[#FECC00] to-amber-500", textColor: "text-[#0F172A]" },
              { name: "Superettan", color: "from-slate-700 to-slate-800", textColor: "text-white" },
              { name: "Division 1", color: "from-blue-500 to-blue-600", textColor: "text-white" },
              { name: "Division 2", color: "from-emerald-500 to-green-600", textColor: "text-white" },
              { name: "Division 3", color: "from-purple-500 to-purple-600", textColor: "text-white" },
              { name: "Division 4", color: "from-orange-500 to-orange-600", textColor: "text-white" },
              { name: "Division 5", color: "from-teal-500 to-teal-600", textColor: "text-white" },
            ].map((div) => (
              <Link
                key={div.name}
                to={`/tabell?division=${encodeURIComponent(div.name)}&season=${selectedSeason}`}
                className="group"
              >
                <div className={`bg-gradient-to-br ${div.color} rounded-xl p-3 md:p-5 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}>
                  <p className={`text-xl md:text-3xl font-black ${div.textColor}`} style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {stats?.divisions_count?.[div.name] || "-"}
                  </p>
                  <p className={`text-xs md:text-sm font-medium ${div.textColor} opacity-80 mt-1`}>{div.name}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
